import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  combineLatest,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  take,
  takeUntil,
} from 'rxjs';
import { BdUiMultiSelectOptionGroupConfig } from './multi-select-option-group.config';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { IftaLabel } from 'primeng/iftalabel';
import { MultiSelect } from 'primeng/multiselect';
import { OverlayOptions } from 'primeng/api';
import { BdAbstractMultiSelect } from '../abstract-multi-select';
import {
  BdAbstractGroupOption,
  BdAbstractOption,
} from '../multi-select.config';
import { BdDataTestDirective } from '@bindoc-projects/util-lib/ui';

@Component({
  selector: 'bd-ui-multi-select-option-group',
  template: `
    @if (variant() === 'toolbar') {
      <ng-container [ngTemplateOutlet]="select"></ng-container>
      @if (errorMessage) {
        <small [id]="selectConfig.id + '-help'">{{ errorMessage }}</small>
      }
    } @else {
      <p-iftalabel>
        <ng-container [ngTemplateOutlet]="select"></ng-container>
        <label [for]="selectConfig.id">{{ displayLabel }}</label>
        @if (errorMessage) {
          <small [id]="selectConfig.id + '-help'">{{ errorMessage }}</small>
        }
      </p-iftalabel>
    }

    <ng-template #select>
      <!-- PrimeNG 22 treats an unset selectAll as "not all selected", so toggle-all could never deselect -->
      <p-multiselect
        [selectAll]="null"
        [inputId]="selectConfig.id"
        [data-test]="selectConfig.id"
        [formControl]="selectCtrl"
        [options]="allOptions | async"
        fluid
        variant="filled"
        [class]="selectStyleClass"
        [placeholder]="triggerPlaceholder"
        [ariaLabel]="triggerPlaceholder"
        [loading]="searching"
        [showClear]="true"
        [filterPlaceHolder]="placeholderLabel"
        [emptyFilterMessage]="noEntriesFoundLabel"
        [emptyMessage]="noEntriesFoundLabel"
        [overlayOptions]="overlayOptions"
        [maxSelectedLabels]="3"
        [selectedItemsLabel]="selectedItemsLabel"
        [showToggleAll]="showFilter"
        [showHeader]="showFilter"
        [group]="true"
        optionGroupLabel="displayName"
        optionGroupChildren="options"
        dataKey="dataKey"
        optionLabel="displayName"
        [filter]="showFilter"
        [filterFields]="['searchTerm']"
        filterBy="searchTerm"
      >
      </p-multiselect>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    IftaLabel,
    MultiSelect,
    NgTemplateOutlet,
    BdDataTestDirective,
  ],
})
export class BdUiMultiSelectOptionGroupComponent<
  TOptionGroup extends { options: TOption[] },
  TOption,
>
  extends BdAbstractMultiSelect<
    TOption,
    BdAbstractOption<TOption>,
    BdAbstractGroupOption<TOptionGroup, TOption>[]
  >
  implements OnDestroy
{
  readonly config =
    input<BdUiMultiSelectOptionGroupConfig<TOptionGroup, TOption>>();
  readonly initialSelection = input<
    Array<TOption> | Observable<Array<TOption>>
  >([]);
  readonly selectOptions = input<Observable<TOptionGroup[]>>(of([]));
  /**
   * Where the select is mounted.
   *
   * `'field'` (default) is the form treatment: a `p-iftalabel` puts the label
   * above the value inside the field, and a bottom margin reserves the row the
   * error message takes once it appears.
   *
   * `'toolbar'` is for control strips — a table caption, a filter bar — where
   * that construction is wrong twice over: the two-line field stands ~14px
   * taller than the plain inputs and buttons beside it, and the reserved error
   * row pushes it off their shared centre line. Here the label moves into the
   * trigger as its placeholder (and its accessible name), leaving a single-line
   * control the same height as its neighbours.
   */
  readonly variant = input<'field' | 'toolbar'>('field');

  readonly selection = output<Array<TOption>>();
  readonly optionsChange = output<Array<TOptionGroup>>();

  public readonly overlayOptions: OverlayOptions = {
    mode: 'overlay',
    appendTo: 'body',
  };

  public showFilter = false;
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);
  private static readonly FILTER_THRESHOLD = 3;

  public get displayLabel(): string {
    const label = this.selectConfig?.selectLabel;
    if (!label) return '';
    return typeof label === 'string' ? label : label['=1'];
  }

  /**
   * Names the control when no `p-iftalabel` does. In the field variant the
   * `<label>` already carries the name, and a placeholder would only repeat it
   * behind the same value.
   */
  public get triggerPlaceholder(): string | undefined {
    return this.variant() === 'toolbar' ? this.displayLabel : undefined;
  }

  /**
   * `mb-4` reserves the row the error `<small>` will take, so a field does not
   * jump when validation turns. A toolbar control has no such row to reserve —
   * the margin would only drop it below the centre line of the strip.
   */
  public get selectStyleClass(): string {
    const classes: string[] = [];
    if ((this.valid || !this.errorMessage) && this.variant() === 'field') {
      classes.push('mb-4');
    }
    if (!this.valid) {
      classes.push('ng-invalid', 'ng-dirty');
    }
    return classes.join(' ');
  }

  public selectConfig: Required<
    BdUiMultiSelectOptionGroupConfig<TOptionGroup, TOption>
  > = {
    id: 'multi-select-option-group',
    selectLabel: 'Element',
    option: {
      uniqueFnc: () => '',
      sortFnc: () => 0,
      searchOptionAccessor: () => '',
      searchOptionDisplayName: () => '',
      cloneFn: (option) => JSON.parse(JSON.stringify(option)),
    },
    optionGroup: {
      uniqueFnc: () => '',
      sortFnc: () => 0,
      searchOptionAccessor: () => '',
      searchOptionDisplayName: () => '',
      cloneFn: (option) => JSON.parse(JSON.stringify(option)),
    },
    emitEventOnInitialSelection: false,
    valid$: new ReplaySubject<{
      isValid: boolean;
      errorMessage?: string;
    }>(1),
  };

  // Event subjects for coordinating initialization
  private _configReady$ = new ReplaySubject<void>(1);
  private _initialSelection$ = new ReplaySubject<Array<TOption>>(1);
  private _optionsLoaded$ = new ReplaySubject<void>(1);

  public constructor() {
    super();

    // Initialize component when config and options are ready (does not wait for initial selection)
    combineLatest([this._configReady$, this._optionsLoaded$])
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.init(this.selectConfig);
        this._cdr.markForCheck();
      });

    // Apply initial selection once a non-empty array arrives AND config + options are ready.
    combineLatest([
      this._configReady$,
      this._optionsLoaded$,
      this._initialSelection$.pipe(
        filter((v) => v.length > 0),
        take(1),
        map((initialValues) =>
          initialValues.map((it) => this.transformToWrapper(it)),
        ),
      ),
    ])
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(([, , wrappedSelection]) => {
        this.setSelection(wrappedSelection, {
          emitEvent: this.selectConfig.emitEventOnInitialSelection,
        });
        this._updateSelectedItemsLabel(wrappedSelection.length);
        this._cdr.markForCheck();
      });

    this.allOptions.pipe(takeUntil(this._onDestroy)).subscribe((groups) => {
      const totalOptions = groups.reduce(
        (sum, group) => sum + group.options.length,
        0,
      );
      this.showFilter =
        totalOptions > BdUiMultiSelectOptionGroupComponent.FILTER_THRESHOLD;
      this._cdr.markForCheck();
    });

    // React to config changes
    effect(() => {
      const config = this.config();
      if (!config) return;
      const configLabel =
        typeof config.selectLabel === 'string'
          ? config.selectLabel
          : config.selectLabel['=1'];
      this.selectConfig = {
        id: `multi-select-option-group-${configLabel}`,
        emitEventOnInitialSelection: false,
        valid$: new ReplaySubject<{
          isValid: boolean;
          errorMessage?: string;
        }>(1),
        ...config,
        option: {
          searchOptionDisplayName: (option) =>
            config.option.searchOptionAccessor(option),
          cloneFn: (option) => JSON.parse(JSON.stringify(option)),
          ...config.option,
        },
        optionGroup: {
          searchOptionDisplayName: (option) =>
            config.optionGroup.searchOptionAccessor(option),
          cloneFn: (option) => JSON.parse(JSON.stringify(option)),
          ...config.optionGroup,
        },
      };
      this._configReady$.next();
    });

    // React to initialSelection changes
    effect(() => {
      const value = this.initialSelection();
      if (value instanceof Observable) {
        value
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe((it) => this._initialSelection$.next(it));
      } else {
        this._initialSelection$.next(value ?? []);
      }
    });

    // React to selectOptions changes
    effect(() => {
      const options$ = this.selectOptions();
      this._loadOptions(options$);
    });
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  protected emptySelection(): BdAbstractOption<TOption>[] {
    return [];
  }

  protected onSelectionChange(selection: BdAbstractOption<TOption>[]): void {
    this.selection.emit(selection.map((it) => it.realValue));
  }

  protected transformToWrapper(option: TOption): BdAbstractOption<TOption> {
    return {
      realValue: option,
      dataKey: this.selectConfig.option.uniqueFnc(option),
      searchTerm: this.selectConfig.option.searchOptionAccessor(option),
      displayName: this.selectConfig.option.searchOptionDisplayName(option),
    };
  }

  private _loadOptions(options$: Observable<TOptionGroup[]>): void {
    this.searching = true;

    options$
      .pipe(
        map((options) =>
          options.map(
            (it) =>
              <BdAbstractGroupOption<TOptionGroup, TOption>>{
                realValue: it,
                dataKey: this.selectConfig.optionGroup.uniqueFnc(it),
                searchTerm:
                  this.selectConfig.optionGroup.searchOptionAccessor(it),
                displayName:
                  this.selectConfig.optionGroup.searchOptionDisplayName(it),
                options: it.options
                  .sort((a, b) => this.selectConfig.option.sortFnc(a, b))
                  .map((child) => this.transformToWrapper(child)),
              },
          ),
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (availableOptions) => {
          this.allOptions.next(
            availableOptions
              .slice()
              .sort((a, b) =>
                this.selectConfig.optionGroup.sortFnc(a.realValue, b.realValue),
              ),
          );
          this.searching = false;
          this.optionsChange.emit(availableOptions.map((it) => it.realValue));
          this._optionsLoaded$.next();
        },
        error: (err) => {
          console.warn(
            `Laden von ${this.selectConfig.selectLabel} nicht möglich`,
            err,
          );
          this.allOptions.next([]);
          this.searching = false;
          this.valid = false;
          this.errorMessage = 'Laden fehlgeschlagen';
          this._optionsLoaded$.next();
          this._cdr.markForCheck();
        },
      });
  }
}
